import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { PenLine } from "lucide-react";
import WorldMap from "./WorldMap";
import { WORLD_DOTS } from "./worldDots";
import { useLang } from "@/i18n/LanguageContext";
import PrelaunchCard from "./PrelaunchCard";
import StatementBand from "./StatementBand";

const Flag = ({ c }) => {
  const [err, setErr] = useState(false);
  if (err) {
    return <span className="w-[21px] shrink-0 text-center font-mono text-[9px] text-ink-muted">{c}</span>;
  }
  return (
    <img
      src={`/flags/${c.toLowerCase()}.png`}
      width="21"
      height="14"
      alt={c}
      className="shrink-0 rounded-[2px] object-cover"
      style={{ boxShadow: "0 0 0 1px rgba(255,255,255,0.14)" }}
      onError={() => setErr(true)}
    />
  );
};

// Messaging-style "typing…" indicator (pencil + bouncing dots)
const TypingDots = () => (
  <span
    className="flex shrink-0 items-center gap-1.5 rounded-full border border-signal2/30 bg-signal2/10 px-2 py-1"
    data-testid="typing-indicator"
  >
    <PenLine className="h-2.5 w-2.5 origin-bottom-left text-signal2 animate-pencil-write" strokeWidth={2.5} />
    <span className="flex items-end gap-[3px] pb-[1px]">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="h-[3px] w-[3px] rounded-full bg-signal2 animate-typing"
          style={{ animationDelay: `${i * 0.18}s` }}
        />
      ))}
    </span>
  </span>
);

const FEED = [
  { c: "IN", n: "Priya S." },
  { c: "KE", n: "Daniel K." },
  { c: "PT", n: "Inês C." },
  { c: "PH", n: "Joseph S." },
  { c: "DE", n: "Markus W." },
  { c: "ES", n: "Lucía F." },
  { c: "NG", n: "Chinedu O." },
  { c: "VN", n: "Nguyen M." },
  { c: "MA", n: "Fatima B." },
  { c: "BR", n: "Ana R." },
  { c: "MX", n: "Carlos M." },
  { c: "ZA", n: "Thabo N." },
  { c: "FR", n: "Camille R." },
  { c: "IT", n: "Marco B." },
  { c: "US", n: "Jessica T." },
  { c: "GB", n: "Oliver H." },
  { c: "CO", n: "Valentina M." },
  { c: "PE", n: "Diego S." },
];

const COORDS = {
  IN: [78, 22], KE: [38, -1], PT: [-8, 39.5], PH: [122, 13], DE: [10, 51],
  ES: [-4, 40], NG: [8, 9.5], VN: [106, 16], MA: [-6, 32], BR: [-51, -11],
  MX: [-102, 24], ZA: [25, -29], FR: [2, 47], IT: [12, 43], US: [-98, 39],
  GB: [-2, 54], CO: [-74, 4], PE: [-76, -10],
};

// 46px row height + 12px gap — feed rows are absolutely stacked so the card
// height never changes (prevents the whole page from shifting on each new member)
const ROW = 58;
const FEED_ROWS = 3;

export const Hero = () => {
  const { t } = useLang();
  const [queue, setQueue] = useState(() =>
    Array.from({ length: FEED_ROWS }, (_, i) => ({ kind: "fake", key: `fake-${i + 1}`, fp: i + 1 }))
  );
  const nextFpRef = useRef(FEED_ROWS + 1);
  const [count, setCount] = useState(null);
  const [realEvents, setRealEvents] = useState([]);
  const [realPulse, setRealPulse] = useState(null);
  const lastCountRef = useRef(null);
  const heroRef = useRef(null);
  const audioCtxRef = useRef(null);
  const audioReadyRef = useRef(false);
  // Sponsor attribution shown in the hero when the visitor arrives via /r/pseudo
  const [invitedBy] = useState(() => {
    try {
      const q = (new URLSearchParams(window.location.search).get("ref") || "").trim().toLowerCase();
      if (q && /^[a-z0-9_]{3,24}$/.test(q)) return q;
      const saved = localStorage.getItem("prenet-ref") || "";
      return /^[a-z0-9_]{3,24}$/.test(saved) ? saved : "";
    } catch {
      return "";
    }
  });
  const reduce = useReducedMotion();

  // Browsers only allow audio after a first user interaction — unlock on first gesture.
  useEffect(() => {
    const unlock = () => {
      audioReadyRef.current = true;
    };
    window.addEventListener("pointerdown", unlock, { once: true });
    window.addEventListener("keydown", unlock, { once: true });
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, []);

  // Discreet two-note chime (Web Audio, no asset) when a real signup lands live.
  const playChime = () => {
    if (!audioReadyRef.current) return;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      audioCtxRef.current = audioCtxRef.current || new Ctx();
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") ctx.resume();
      const now = ctx.currentTime;
      const master = ctx.createGain();
      master.gain.setValueAtTime(0.0001, now);
      master.gain.exponentialRampToValueAtTime(0.09, now + 0.02);
      master.gain.exponentialRampToValueAtTime(0.0001, now + 0.55);
      master.connect(ctx.destination);
      [[880, 1], [1318.51, 0.55]].forEach(([freq, vol]) => {
        const osc = ctx.createOscillator();
        const og = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = freq;
        og.gain.value = vol;
        osc.connect(og);
        og.connect(master);
        osc.start(now);
        osc.stop(now + 0.55);
      });
    } catch (e) {}
  };

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const parallaxY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  useEffect(() => {
    const id = setInterval(() => {
      const fp = nextFpRef.current++;
      setQueue((q) => [...q.slice(-2), { kind: "fake", key: `fake-${fp}`, fp }]);
    }, 3000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/registrations/count`, { cache: "no-store" })
      .then((r) => r.json())
      .then((d) => setCount(d.count))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const poll = () => {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/registrations/count`, { cache: "no-store" })
        .then((r) => r.json())
        .then((d) => {
          setCount(d.count);
          if (lastCountRef.current !== null && d.count > lastCountRef.current) {
            const added = [];
            for (let p = lastCountRef.current + 1; p <= d.count; p++) {
              added.push({ id: `${p}-${Date.now()}`, position: p, at: Date.now() });
            }
            setRealEvents((ev) => [...added, ...ev].slice(0, 3));
            // Real signups join the same bottom-to-top conveyor as the illustrative feed
            const keep = Math.max(0, FEED_ROWS - added.length);
            setQueue((q) => [
              ...(keep ? q.slice(-keep) : []),
              ...added.map((a) => ({ kind: "real", key: `real-${a.id}`, position: a.position })),
            ]);
            playChime();
          }
          lastCountRef.current = d.count;
        })
        .catch(() => {});
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") poll();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    poll();
    const id = setInterval(poll, 10000);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, []);

  useEffect(() => {
    if (realEvents.length) {
      const spot = WORLD_DOTS[Math.floor(Math.random() * WORLD_DOTS.length)];
      setRealPulse({ lon: spot[0], lat: spot[1], key: realEvents[0].id });
    }
  }, [realEvents]);

  const lastFake = [...queue].reverse().find((e) => e.kind !== "real") || queue[queue.length - 1];
  const entry = FEED[lastFake.fp % FEED.length];
  const nowTick = Date.now();
  const pillReal = realEvents.length && nowTick - realEvents[0].at < 20000 ? realEvents[0] : null;
  const pulseCoord = COORDS[entry.c] || [10, 45];
  const pulse = { lon: pulseCoord[0], lat: pulseCoord[1], key: lastFake.key };

  return (
    <section id="top" ref={heroRef} className="noise relative flex min-h-screen items-center overflow-hidden">
      <WorldMap className="absolute inset-0 h-full w-full" pulse={pulse} extraPulse={realPulse} />
      <div className="panel-line pointer-events-none absolute inset-0 opacity-60" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,#0A1220_78%)]" />

      <div className="relative z-10 w-full">
      <motion.div
        style={reduce ? {} : { y: parallaxY, opacity: fade }}
        className="relative z-10 mx-auto w-full max-w-6xl px-5 pt-24 sm:px-8 lg:pt-28"
      >
        <PrelaunchCard invitedBy={invitedBy} count={count} />
      </motion.div>

      <StatementBand />

      <div className="relative z-10 mx-auto mt-8 w-full max-w-7xl px-5 pb-24 sm:px-8 lg:mt-10 lg:pb-16" data-testid="hero-live-strip">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 lg:flex-row lg:items-stretch lg:gap-8">
        <div className="flex min-w-0 flex-1 flex-col justify-center gap-8">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.95 }}
          className="flex flex-wrap items-center gap-x-10 gap-y-4"
        >
          <div>
            <div className="font-display text-2xl font-bold text-ink sm:text-3xl" data-testid="hero-registration-count">
              {count === null ? "—" : count.toLocaleString()}
            </div>
            <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-subtle">
              {t.hero.positioned}
            </div>
          </div>
          <div className="h-10 w-px bg-white/10" />
          <div>
            <div className="font-display text-2xl font-bold text-ink sm:text-3xl">04</div>
            <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-subtle">
              {t.hero.statStructures}
            </div>
          </div>
          <p className="w-full font-mono text-[10px] uppercase tracking-[0.18em] text-ink-subtle sm:w-auto">
            {t.form.note}
          </p>
        </motion.div>
        </div>

        <div className="w-full lg:w-[400px] lg:shrink-0">
          <div className="glass rounded-2xl p-5 [overflow-anchor:none]" data-testid="members-panel">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="flex items-center gap-2.5">
                <span className="h-2 w-2 rounded-full bg-signal2 animate-pulse-soft" />
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">
                  {t.hero.liveLabel}
                </span>
                <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-ink-subtle">
                  {t.hero.liveNote}
                </span>
              </span>
              {pillReal ? (
                <motion.span
                  key={`real-${pillReal.id}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.4 }}
                  className="flex items-center gap-1.5 font-mono text-[11px] tracking-[0.12em] text-signal2"
                  data-testid="hero-live-feed"
                >
                  <span className="h-2 w-2 rounded-full bg-signal2 animate-pulse-soft" />
                  <span>
                    #{pillReal.position} {t.hero.realJoin}
                  </span>
                </motion.span>
              ) : (
                <motion.span
                  key={lastFake.key}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.4 }}
                  className="flex items-center gap-1.5 font-mono text-[11px] tracking-[0.12em] text-signal"
                  data-testid="hero-live-feed"
                >
                  <Flag c={entry.c} />
                  <span>{entry.n}</span>
                </motion.span>
              )}
            </div>
            <div className="relative mt-4 h-[162px] overflow-hidden" data-testid="members-feed">
              <AnimatePresence initial={false}>
                {queue.map((item, i) => {
                  const isReal = item.kind === "real";
                  const m = isReal ? null : FEED[item.fp % FEED.length];
                  return (
                    <motion.div
                      key={item.key}
                      initial={{ opacity: 0, y: ROW, top: i * ROW }}
                      animate={{ opacity: 1, y: 0, top: i * ROW }}
                      exit={{ opacity: 0, y: -ROW }}
                      transition={{ duration: 0.45, ease: "easeInOut" }}
                      data-testid={isReal ? `member-real-row-${i}` : `member-row-${i}`}
                      className={
                        isReal
                          ? "absolute inset-x-0 flex h-[46px] items-center gap-3 rounded-xl border border-signal2/30 bg-signal2/5 px-3.5"
                          : "absolute inset-x-0 flex h-[46px] items-center gap-3 rounded-xl border border-white/5 bg-night-2/60 px-3.5"
                      }
                    >
                      {isReal ? (
                        <>
                          <span className="h-2 w-2 shrink-0 rounded-full bg-signal2 animate-pulse-soft" />
                          <span className="font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-signal2">
                            Live
                          </span>
                          <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink">
                            #{item.position} {t.hero.realJoin}
                          </span>
                        </>
                      ) : (
                        <>
                          <Flag c={m.c} />
                          <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink">{m.n}</span>
                          {i === queue.length - 1 && <TypingDots />}
                        </>
                      )}
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
            <p
              className="mt-4 border-t border-white/8 pt-3.5 text-xs font-medium leading-relaxed text-tactical"
              data-testid="members-urgent-note"
            >
              {t.hero.powerlineNote}
            </p>
          </div>
        </div>
        </div>
      </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2"
      >
        <div className="flex h-10 w-6 items-start justify-center rounded-full border border-white/15 p-1.5">
          <motion.span
            animate={{ y: [0, 14, 0] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
            className="h-2 w-1 rounded-full bg-signal"
          />
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
